import * as React from 'react'
import NewsItems from '../../components/Messenger/News/NewsItems'
import NewsItem from '../../components/Messenger/News/NewsItem'
import Navigation from '../../components/Messenger/Navigation'
import CloseButton from '../../components/Messenger/Navigation/CloseButton'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { NewsCenterController } from '../../controllers'
import { News as NewsType } from '../../../types'
import { useSelector } from 'react-redux'
import { AppState } from '../../store'
import { useDebouncedCallback } from 'use-debounce'
import FadeInContainer from '../../components/Messenger/Animation/FadeInContainer'
import ScrollToTopOnMount from '../../effects/ScrollToTopOnMount'

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 12px;
`

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const RightContainer = styled.div``

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`

const Metadata = styled.sub`
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
`

interface IProps {
  onNavigate: () => void
}

interface IState {
  news: NewsType[]
  currentPage: number,
  totalPages: number
  didInitialLoad: boolean
  isFetching: boolean
  endReached: boolean
}

const News = (props: IProps) => {
  const { t } = useTranslation()
  const widget = useSelector((state: AppState) => state.application.widget)
  const locale = useSelector((state: AppState) => state.application.locale)
  const scrollContainer = React.useRef<HTMLDivElement>()

  const [state, setState] = React.useState<IState>({
    news: [],
    currentPage: 0,
    totalPages: 0,
    didInitialLoad: true,
    endReached: false,
    isFetching: false,
  })

  const { news, currentPage, totalPages, endReached, isFetching } = state

  const fetchNews = async (page: number) => {
    try {
      const { news: responseNews, current_page, total_pages } = await NewsCenterController.getNews({
        page: page,
        per_page: 5,
      })

      setState({
        ...state,
        news: page === 1 ? [...responseNews] : [...news, ...responseNews],
        currentPage: current_page,
        totalPages: total_pages,
        endReached: current_page === total_pages,
        didInitialLoad: true,
        isFetching: false,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  const onScroll = useDebouncedCallback(() => {
    const element = scrollContainer.current
    if (!element) return

    const scrolledHeight = element.scrollHeight - element.scrollTop - 300
    const containerHeight = element.clientHeight

    if (element && scrolledHeight <= containerHeight && !isFetching && !endReached) {
      fetchNews(currentPage + 1)
    }
  }, 150);

  React.useEffect(() => {
    fetchNews(1)
  }, [])

  return (
    <FadeInContainer>
      <Navigation
        title={t('News::News')}
        right={<CloseButton />}
      />
      <ScrollToTopOnMount />

      <NewsItems ref={scrollContainer} onScroll={onScroll}>
        <HeaderContainer>
          <HeaderWrapper>
            <LeftContainer>
              <Title>{t('News::Latest')}</Title>
              <Metadata>{t('News::From team {{teamName}}', { teamName: widget.name })}</Metadata>
            </LeftContainer>

            <RightContainer></RightContainer>
          </HeaderWrapper>
        </HeaderContainer>

        {news.map((newsItem) => (
          <NewsItem
            key={newsItem.id}
            news={newsItem}
            locale={locale}
          />
        ))}
      </NewsItems>
    </FadeInContainer>
  )
}

export default News